import React from 'react';

import {
	EvEllipsedTextWithTooltip,
	EVINCED_SEVERITIES,
	EvInfoTooltip,
	EvLink,
	EvLinkTypes,
	EvTableColumn,
	EvTableOptions,
	SORT_ORDER,
	TableSortOption
} from '@evinced-private/ui-common';

import { EvLinkLocal } from '../../../../components/common/ev-link-local/EvLinkLocal';
import { renderNumberCellFormatter } from '../../../../components/common/ev-table/cell-formatters/numberCellFormatter';
import { ScanTableDefinition } from '../../../../components/scan-analysis/base/BaseScanViewV2';
import { RoutesHelper } from '../../../../helpers/RoutesHelper';
import { CustomSeverityNames, resolveSeverityLabel } from '../../../../helpers/SeverityLabelHelper';
import { PageData } from '../../../../interfaces/PagesPaginationResponse';

import {
	buildOtherSeveritiesTooltipText,
	OTHER_SEVERITIES_COLUMN_LABEL
} from './ScanPagesTableHelperV2.constants';

const PAGE_TITLE_MAX_LENGTH = 180;
const PAGE_TITLE_MAX_LENGTH_STR = `${PAGE_TITLE_MAX_LENGTH}px`;

const URL_COLUMN_WIDTH = '400px';
const ACTIONS_COLUMN_WIDTH = '140px';
const ISSUES_COLUMN_WIDTH = '100px';

const URL_COLUMN_STYLE = { width: URL_COLUMN_WIDTH, maxWidth: URL_COLUMN_WIDTH };
const ACTIONS_COLUMN_STYLE = { width: ACTIONS_COLUMN_WIDTH };
const ISSUES_COLUMN_STYLE = { width: ISSUES_COLUMN_WIDTH };

export const getDefaultSort = (): TableSortOption => ({
	dataField: 'totalIssues',
	order: SORT_ORDER.DESC
});

const getColumns = (
	propertyId: string,
	scanId: string,
	pageId: string | undefined,
	customSeverityNames: CustomSeverityNames = {}
): EvTableColumn[] => {
	const criticalSeriousMerged =
		resolveSeverityLabel(EVINCED_SEVERITIES.CRITICAL, customSeverityNames) ===
		resolveSeverityLabel(EVINCED_SEVERITIES.SERIOUS, customSeverityNames);

	// Note: numeric values for these columns are wrong for tenants with custom severities until
	// backend ticket P-B3 lands (per-page bucket counts honoring custom severity mapping).
	// The labels below are correctly resolved already.
	return [
		{
			dataField: 'url',
			text: 'URL',
			style: URL_COLUMN_STYLE,
			headerStyle: URL_COLUMN_STYLE,
			sort: true,
			formatter: (url: string) => {
				return (
					<EvLinkLocal
						linkText={url}
						url={RoutesHelper.getUrlDrilldownPath(propertyId, scanId, url)}
						customTooltip={{ dataTip: url, dataFor: 'table-tooltip' }}
						type={EvLinkTypes.DARK}
						pageId={pageId}
					/>
				);
			}
		},
		{
			dataField: 'pageTitle',
			text: 'Page Title',
			style: { maxWidth: PAGE_TITLE_MAX_LENGTH_STR },
			headerStyle: { maxWidth: PAGE_TITLE_MAX_LENGTH_STR },
			sort: true,
			formatter: (pageTitle: string) => {
				let formattedPageTitle: string;
				if (pageTitle === '') {
					formattedPageTitle = 'Untitled';
				} else {
					formattedPageTitle = pageTitle || 'N/A';
				}

				return (
					<EvEllipsedTextWithTooltip
						text={formattedPageTitle}
						maxWidth={140}
						showTooltipOnlyOnEllipsed
					/>
				);
			}
		},
		{
			dataField: 'totalIssues',
			text: 'Total issues',
			style: ISSUES_COLUMN_STYLE,
			headerStyle: ISSUES_COLUMN_STYLE,
			sort: true,
			formatter: renderNumberCellFormatter
		},
		{
			dataField: 'critical',
			text: resolveSeverityLabel(EVINCED_SEVERITIES.CRITICAL, customSeverityNames),
			style: ISSUES_COLUMN_STYLE,
			headerStyle: ISSUES_COLUMN_STYLE,
			sort: true,
			formatter: renderNumberCellFormatter
		},
		...(!criticalSeriousMerged
			? [
					{
						dataField: 'serious',
						text: resolveSeverityLabel(EVINCED_SEVERITIES.SERIOUS, customSeverityNames),
						style: ISSUES_COLUMN_STYLE,
						headerStyle: ISSUES_COLUMN_STYLE,
						sort: true,
						formatter: renderNumberCellFormatter
					} as EvTableColumn
				]
			: []),
		{
			dataField: 'otherSeverities',
			text: OTHER_SEVERITIES_COLUMN_LABEL,
			style: ISSUES_COLUMN_STYLE,
			headerStyle: ISSUES_COLUMN_STYLE,
			sort: false,
			headerFormatter: () => (
				<EvInfoTooltip title={OTHER_SEVERITIES_COLUMN_LABEL} showTitleText tooltipPlace="top">
					{buildOtherSeveritiesTooltipText(customSeverityNames)}
				</EvInfoTooltip>
			),
			formatter: renderNumberCellFormatter
		},

		{
			dataField: 'url',
			text: 'Actions',
			style: ACTIONS_COLUMN_STYLE,
			headerStyle: ACTIONS_COLUMN_STYLE,
			sort: false,
			formatter: (url: string) => {
				return (
					<EvLink
						url={url}
						linkText="Open page"
						type={EvLinkTypes.DARK}
						openInNewTab={true}
						showIcon={true}
					/>
				);
			}
		}
	];
};

const getOptions = (): EvTableOptions => {
	return {
		title: 'Scan pages',
		caption: 'Scan pages table',
		pagination: true,
		dataType: 'pages with issues',
		remote: true,
		defaultSorted: [getDefaultSort()]
	};
};

type PagesTableRowData = {
	url: string;
	totalIssues: number;
	critical: number;
	serious: number;
	otherSeverities: number;
};
const getTableData = (
	pages: PageData[],
	customSeverityNames: CustomSeverityNames = {}
): PagesTableRowData[] => {
	const criticalSeriousMerged =
		resolveSeverityLabel(EVINCED_SEVERITIES.CRITICAL, customSeverityNames) ===
		resolveSeverityLabel(EVINCED_SEVERITIES.SERIOUS, customSeverityNames);

	return pages.map((p) => {
		return {
			id: p.url,
			url: p.url,
			pageTitle: p.pageTitle,
			totalIssues: p.totalIssues || 0,
			critical: criticalSeriousMerged ? (p.critical || 0) + (p.serious || 0) : p.critical || 0,
			serious: criticalSeriousMerged ? 0 : p.serious || 0,
			otherSeverities:
				(p.moderate || 0) + (p.minor || 0) + (p.needsReview || 0) + (p.bestPractice || 0)
		};
	});
};

export const getTableDefinition = (
	pages: PageData[],
	propertyId: string,
	scanId: string,
	totalCount: number,
	totalPaginationableResults: number,
	pageId?: string,
	customSeverityNames?: CustomSeverityNames
): ScanTableDefinition => {
	const names = customSeverityNames ?? {};
	const options = getOptions();
	const result: ScanTableDefinition = {
		columns: getColumns(propertyId, scanId, pageId, names),
		options,
		data: getTableData(pages, names),
		totalCount,
		totalPaginationableResults
	};
	return result;
};
